import * as React from 'react';
import setCaretAtTheEnd from '../../../services/mini/setCaretAtTheEnd';

type InlineEditorProps = {
    editMode?: boolean
    value: string;
    name?: string
    id?: string
    withBorder?: boolean
    exitEditModeAfterConfirm?: boolean
    confirmAction: (newVal: string) => void;
    contentClass?: string
    inputClass?: string
    className?: string
    autoFocus?: boolean
    placeholder?: string
    readOnly?: boolean
    maxLength?: number
    confirmOnLeave?: boolean
    onEnterEditMode?: () => void;
    onLeaveEditMode?: () => void;
    validate?: (newVal: string) => { error?: string, info?: string };
    beforeChangeValidation?: (newVal: string) => boolean;
};

type InlineEditorState = {
    editMode: boolean
    value: string
    isActive: boolean
    error?: string
    info?: string
};

class InlineEditor extends React.PureComponent<InlineEditorProps, InlineEditorState> {
    constructor(props) {
        super(props);
        this.state = {
            editMode: this.props.editMode,
            value: this.props.value,
            isActive: false,
        };
    }

    componentDidMount() {
        const {
            autoFocus,
        } = this.props;

        if (autoFocus) {
            setTimeout(() => {
                setCaretAtTheEnd(this.refs.input);
            });
        }

        document.addEventListener('mousedown', this.handleClickOutsideCb, true);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutsideCb, true);
    }

    enterEditMode = () => {
        const {
            autoFocus,
        } = this.props;

        this.setState({
            editMode: true,
            value: this.props.value,
            error: null,
            info: null
        });

        if (autoFocus) {
            setTimeout(() => {
                setCaretAtTheEnd(this.refs.input);
            });
        }

        if (this.props.onEnterEditMode) {
            this.props.onEnterEditMode();
        }
    }

    validate = (newVal) => {
        if (!this.props.validate) return true;

        const newState = this.props.validate(newVal);

        if (Object.keys(newState).length === 0) {
            this.setState({ error: null, info: null });
            return true;
        }
        this.setState(newState);
        if (newVal.length > this.props.maxLength) {
            return false;
        }
        if (!newState.error) {
            return true;
        }
        return false;
    }

    cancelEditMode = () => {
        this.setState({
            editMode: false,
            value: this.props.value,
            error: null,
            info: null
        });
        if (this.props.onLeaveEditMode) {
            this.props.onLeaveEditMode();
        }
    }

    onChange = (e) => {
        const newValue = e.target.value;
        if (this.props.beforeChangeValidation) {
            if (this.props.beforeChangeValidation(newValue)) {
                this.setState({ value: newValue });
                this.validate(newValue);
            }
        } else {
            this.setState({ value: newValue });
            this.validate(newValue);
        }
    }

    onFocus = () => {
        this.setState({ isActive: true });
    }

    onBlur = () => {
        this.setState({ isActive: false });
    }

    handleClickOutsideCb = (e) => {
        if (!e.target.closest('.inline-editor')) {
            if (this.props.confirmOnLeave && this.state.editMode) {
                this.confirmAction();
                return;
            }
            this.cancelEditMode();
        }
    }

    onKeyDown = (event) => {
        const keyCode = event.keyCode || event.which;
        if (keyCode === 13) {
            this.confirmAction();
        } else if (keyCode === 27) {
            this.cancelEditMode();
        }
    }

    onKeyUp = () => {
    }

    confirmAction = () => {
        if (this.state.value === this.props.value) {
            this.cancelEditMode();
        } else {
            if (this.validate(this.state.value)) {
                this.props.confirmAction(this.state.value);
                this.setState({ error: null, info: null });
                if (this.props.exitEditModeAfterConfirm) {
                    this.cancelEditMode();
                }
            }
        }
    }

    render() {
        const {
            inputClass,
            placeholder,
            className,
            name,
            readOnly,
            maxLength,
            id,
            contentClass,
            withBorder
        } = this.props;
        const { editMode, value, isActive, error, info } = this.state;
        return (
            <div className={`inline-editor ${readOnly && 'readonly'} ${className}`}>
                {editMode ?
                    <div className={`inline-editor_content edit-mode ${contentClass} ${isActive && 'active'} ${(info || error) && 'error'}`}>
                        {(info && !error) && <span className="inline-editor_info">{info}</span>}
                        {error && <span className="inline-editor_error">{error}</span>}
                        <input type="text"
                            ref="input"
                            className={`${inputClass}`}
                            placeholder={placeholder}
                            value={value}
                            name={name}
                            id={id}
                            onFocus={this.onFocus}
                            onKeyDown={this.onKeyDown}
                            onKeyUp={this.onKeyUp}
                            onBlur={this.onBlur}
                            onChange={this.onChange} />
                        <div className="inline-editor_content_commands">
                            <i className="icon icon-alan-ok"
                                onClick={this.confirmAction} />
                            <i className="icon icon-alan-remove" onClick={this.cancelEditMode} />
                        </div>
                    </div> :
                    <div className={`inline-editor_content view-mode ${contentClass} ${withBorder && 'with-border'}`}
                        onClick={this.enterEditMode}>
                        <span className="inline-editor_content_text">{value}</span>
                        <i className="icon icon-alan-edit" />
                    </div>}
            </div>
        );
    }
}

export default InlineEditor;